import React, { useState, useEffect } from 'react';
import { CheckCircle, MailX, Settings, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const UnsubscribePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Unsubscribe - ContaX';
    
    // Get email from URL params if available
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }

    // Track page view
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity('set', 'page', 'unsubscribe');
    }
  }, [searchParams]);

  const handleUnsubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // In development, simulate the process
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Remove from localStorage
        const waitlistData = JSON.parse(localStorage.getItem('contax-waitlist') || '[]');
        const updatedData = waitlistData.filter((entry: any) => entry.email !== email);
        localStorage.setItem('contax-waitlist', JSON.stringify(updatedData));
        localStorage.removeItem('contax-user-email');
        
        setIsUnsubscribed(true);
        return;
      }

      // Production: Submit unsubscribe request
      const response = await fetch('/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          'form-name': 'contax-unsubscribe',
          'email': email,
          'timestamp': new Date().toISOString(),
          'action': 'unsubscribe'
        }).toString()
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe');
      }

      // Track unsubscribe
      if (typeof window !== 'undefined' && (window as any).clarity) {
        (window as any).clarity('set', 'unsubscribe', email);
      }

      setIsUnsubscribed(true);
    } catch (err) {
      setError('Failed to unsubscribe. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isUnsubscribed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Successfully Unsubscribed
          </h1>
          
          <p className="text-gray-600 mb-6">
            You've been removed from the ContaX waitlist. You won't receive any more emails from us.
          </p>
          
          <div className="space-y-4">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Homepage
            </button>
            
            <p className="text-xs text-gray-500">
              Changed your mind? You can always rejoin our waitlist anytime.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to ContaX
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MailX className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Unsubscribe from ContaX
            </h1>
            
            <p className="text-gray-600">
              We're sorry to see you go. Enter your email address to unsubscribe from our waitlist.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleUnsubscribe} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
                placeholder="Enter your email address"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Unsubscribe from Waitlist'
              )}
            </button>
          </form>

          {/* Feedback Section */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start">
              <Settings className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-gray-700 mb-2">
                  <strong>Before you go:</strong> We'd love to know why you're unsubscribing to help us improve.
                </p>
                <a 
                  href="mailto:feedback@contax.com?subject=Unsubscribe Feedback"
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Send us feedback
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              You can always rejoin our waitlist if you change your mind.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UnsubscribePage;